import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule, Title, Meta, provideClientHydration } from '@angular/platform-browser';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';

import { environment } from './../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { FooterModule } from './shared/components/footer/footer.module';
import { FooterV2Module } from './shared/components/footer-v2/footer-v2.module';

import { HeaderModule } from './shared/components/header/header.module';
import { HeaderV2Module } from './shared/components/header-v2/header-v2.module';

import { ModalsModule } from './modals/modals.module';
import { PagesModule } from './pages/pages.module';
import { SharedModule } from './shared/shared.module';

import { httpInterceptorProviders } from './interceptors';
import { GoogleMapsModule } from '@angular/google-maps';
import { CloudinaryModule } from '@cloudinary/ng';

import { NgcCookieConsentModule, NgcCookieConsentConfig } from 'ngx-cookieconsent';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { ACCESS_TOKEN_KEY } from './constants/app.constants';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ConfigService } from './services/config/config.service';
import { FeatureFlagsService } from './services/feature-flag/feature-flag.service';
import { appInitializerFactory } from './app-init.factory';
import { AuthManagerService } from './services/auth-manager/auth-manager.service';

declare global {
  interface Window {
    analytics: any;
  }
}

export function jwtOptionsFactory(platformId: Object) {
  return {
    tokenGetter: () => {
      if (isPlatformBrowser(platformId)) {
        return localStorage.getItem(ACCESS_TOKEN_KEY);
      }
    },
    allowedDomains: [environment.apiDomain],
    disallowedRoutes: [`${environment.apiUrl}/token`],
  };
}

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: environment.domain,
  },
  position: 'bottom',
  theme: 'classic',
  palette: {
    popup: {
      background: '#000000',
      text: '#ffffff',
      link: '#ffffff',
    },
    button: {
      background: '#07a550',
      text: '#ffffff',
      border: 'transparent',
    },
  },
  type: 'opt-in',
  content: {
    message: 'This website uses cookies to ensure you get the best experience on our website.',
    dismiss: 'Got it!',
    deny: 'Refuse cookies',
    link: 'Learn more',
    href: '/cookies',
    policy: 'Cookie Policy',
    target: '_self',
  },
  revokeBtn: '<div></div>',
};

const providersArray = [
  httpInterceptorProviders,
  Title,
  Meta,
  NgbActiveModal,
  provideHttpClient(withInterceptorsFromDi(), withFetch()),
  ConfigService,
  FeatureFlagsService,
  ...(environment.googleTagManagerId
    ? [{ provide: 'googleTagManagerId', useValue: environment.googleTagManagerId }]
    : []),
  {
    provide: APP_INITIALIZER,
    useFactory: appInitializerFactory,
    deps: [ConfigService, PLATFORM_ID],
    multi: true,
  },
  {
    provide: APP_INITIALIZER,
    useFactory: (authManager: AuthManagerService) => () => authManager.loginBasedOnB2CCookie(),
    deps: [AuthManagerService],
    multi: true,
  },
];

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserModule,
    FooterModule,
    FooterV2Module,
    HeaderModule,
    HeaderV2Module,
    ModalsModule,
    NgcCookieConsentModule.forRoot(cookieConfig),
    PagesModule,
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [PLATFORM_ID],
      },
    }),
    CloudinaryModule,
    SharedModule,
    NoopAnimationsModule,
    GoogleMapsModule,
  ],
  providers: providersArray,
})
export class AppModule {}
